import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { useFlag } from '@unleash/proxy-client-react';
import { openModal } from '../redux/slices/modals';
import { selectPrivyToken } from '../redux/slices/auth';
import { useGetCurrentUserProfileQuery } from '../redux/api/authorized/auth';

// Coinbase charges 2.5% fee on Apple Pay deposits
const APPLE_PAY_FEE_RATE = 0.025;
// Buffer to account for floating point precision and USDC fluctuations
// Add $0.10 buffer minimum, plus 1% to handle fluctuations and ensure we always have enough
const BUFFER_AMOUNT = 0.10;
const BUFFER_PERCENTAGE = 0.01; // 1% buffer for safety
// Tolerance for balance checks to account for floating point precision and minor rounding
// This is a small safety net just for comparison, not to allow insufficient funds
const BALANCE_TOLERANCE = 0.05; // Allow $0.05 tolerance for rounding/precision only

/**
 * Calculate the deposit amount needed to receive the desired amount after fees.
 * If you want $25 after fees and Coinbase charges 2.5%, we need to deposit enough
 * to account for fees plus a small buffer for precision and USDC fluctuations.
 * Formula: (desiredAmount / (1 - feeRate)) + buffer
 */
export const calculateApplePayAmountWithFees = (desiredAmount) => {
  const amount = typeof desiredAmount === 'number' ? desiredAmount : parseFloat(desiredAmount ?? '0');
  if (!amount || Number.isNaN(amount) || amount <= 0) {
    return amount;
  }

  // Calculate base amount with fees
  const baseAmountWithFees = amount / (1 - APPLE_PAY_FEE_RATE);

  // Add buffer: $0.10 minimum + 0.5% of the desired amount
  // This ensures we have enough even with small USDC fluctuations
  const buffer = BUFFER_AMOUNT + (amount * BUFFER_PERCENTAGE);

  // Round up to 2 decimal places to avoid precision issues
  const totalDepositAmount = Math.ceil((baseAmountWithFees + buffer) * 100) / 100;

  return totalDepositAmount;
};

/**
 * Calculate the fee amount for a given base amount (the amount you want to receive).
 * If you want to receive $X after Apple Pay takes their 2.5% fee from the charged amount,
 * you need to charge: X / (1 - 0.025) = X / 0.975
 * Fee = total - baseAmount = (baseAmount / 0.975) - baseAmount
 *
 * @param {number} baseAmount - The amount you want to receive (gift + shipping)
 * @returns {number} - The fee amount that will be charged
 */
export const calculateApplePayFee = (baseAmount) => {
  const amount = typeof baseAmount === 'number' ? baseAmount : parseFloat(baseAmount ?? '0');
  if (!amount || Number.isNaN(amount) || amount <= 0) {
    return 0;
  }

  // Calculate the total amount user should be charged
  // Fee is taken from the total, so: total = baseAmount / (1 - feeRate)
  const total = amount / (1 - APPLE_PAY_FEE_RATE);
  const fee = total - amount;

  return fee;
};

/**
 * Calculate Apple Pay fee and total amount correctly.
 * Uses calculateApplePayFee internally for consistency.
 *
 * @param {number} baseAmount - The amount you want to receive (gift + shipping)
 * @returns {Object} - { fee: number, total: number }
 *   - fee: The fee amount that will be charged
 *   - total: The total amount the user will be charged (baseAmount + fee)
 */
export const calculateApplePayFeeAndTotal = (baseAmount) => {
  const amount = typeof baseAmount === 'number' ? baseAmount : parseFloat(baseAmount ?? '0');
  if (!amount || Number.isNaN(amount) || amount <= 0) {
    return { fee: 0, total: 0 };
  }

  const fee = calculateApplePayFee(amount);
  const total = amount + fee;

  return { fee, total };
};

const useApplePay = () => {
  const dispatch = useDispatch();
  const privyToken = useSelector(selectPrivyToken);
  const enableApplePay = useFlag('enableApplePay');
  const enableApplePayWeb = useFlag('enableApplePayWeb');
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const isNativeIOS =
    Capacitor.isNativePlatform() &&
    Capacitor.isPluginAvailable('HiddenWebview') &&
    Capacitor.getPlatform() === 'ios';
  const isWeb = !Capacitor.isNativePlatform();

  // Web Apple Pay (iframe) is behind enableApplePayWeb; native is unchanged
  const applePayEnabled = enableApplePay && (isNativeIOS || (isWeb && enableApplePayWeb));

  const ensurePhoneVerified = useCallback(
    (onContinue) => {
      const proceed = () => {
        if (typeof onContinue === 'function') {
          onContinue();
        }
      };

      if (currentUser?.phoneNumber) {
        proceed();
      } else {
        dispatch(
          openModal({
            name: 'verifyPhoneNumber',
            props: { onContinue: proceed },
          }),
        );
      }
    },
    [currentUser?.phoneNumber, dispatch],
  );

  const handleApplePay = useCallback(() => {
    if (!applePayEnabled) return;

    ensurePhoneVerified(() => {
      dispatch(
        openModal({
          name: 'amountSelector',
          props: {
            minAmount: 2,
            onContinue: (amount) => {
              dispatch(
                openModal({
                  name: 'applePay',
                  props: { amount },
                }),
              );
            },
          },
        }),
      );
    });
  }, [applePayEnabled, dispatch, ensurePhoneVerified]);

  return {
    handleApplePay,
    applePayEnabled,
    ensurePhoneVerified,
    calculateAmountWithFees: calculateApplePayAmountWithFees,
    calculateFee: calculateApplePayFee,
    feeRate: APPLE_PAY_FEE_RATE,
    balanceTolerance: BALANCE_TOLERANCE,
  };
};

export { BALANCE_TOLERANCE, APPLE_PAY_FEE_RATE };

export default useApplePay;
